import {
  Accordion,
  Box,
  Button,
  Flex,
  JaneGoldIcon,
  Typography,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';

import type { JaneGoldCartCalloutProps } from '@jane/kiosk/components/lib/JaneGoldCallout/JaneGoldCallout.types';
import { LineItem } from '@jane/kiosk/components/lib/LineItem/LineItem';
import { useKioskSpacing } from '@jane/kiosk/hooks';

import styles from '../JaneGoldCallout.module.css';

export const JaneGoldCartCallout = ({
  brandDiscounts,
  brandDiscountsServiceFeeTotal,
  brandDiscountsTotal,
  onLinkBankClick,
  onLoginClick,
  showJanePay,
  userHasLinkedBank,
  userSignedIn,
}: JaneGoldCartCalloutProps) => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  return (
    <Box
      className={styles['boxShadow']}
      borderRadius="sm"
      p={getKioskSpacing(24, 16)}
      mb={getKioskSpacing(24, 16)}
    >
      {userSignedIn && userHasLinkedBank ? (
        <Accordion.Item id="jane-gold-cash-back">
          <Accordion.Heading>
            <Flex flexDirection="row" alignItems="center">
              <JaneGoldIcon size="lg" mr={16} />
              <Typography branded as="span" mr={getKioskSpacing(72, 24)}>
                Cash back after purchase
              </Typography>
              <Flex ml="auto" alignItems="center" gap={8}>
                <Typography branded variant="body-semibold">
                  {formatCurrency(brandDiscountsTotal)}
                </Typography>
                <Accordion.Indicator iconSize="md" />
              </Flex>
            </Flex>
          </Accordion.Heading>
          <Accordion.Content destroyOnClose>
            <Flex flexDirection="column" ml={64} mt={16}>
              {brandDiscounts.map((discount) => (
                <LineItem
                  key={discount.special_title}
                  amount={
                    <Typography branded>
                      +{formatCurrency(discount.discount_total_with_fee)}
                    </Typography>
                  }
                  label={`${discount.special_title}
                      ${
                        discount.product_brand?.name
                          ? ` (${discount.product_brand.name})`
                          : ''
                      }`}
                  mb={0}
                />
              ))}
              <LineItem
                amount={
                  <Typography branded>
                    {formatCurrency(brandDiscountsServiceFeeTotal)}
                  </Typography>
                }
                label="Service fee"
                mb={0}
              />
              <LineItem
                amount={
                  <Typography variant="body-semibold" branded>
                    +{formatCurrency(brandDiscountsTotal)}
                  </Typography>
                }
                label={
                  <Typography variant="body-semibold" branded>
                    Estimated total cash back
                  </Typography>
                }
                mb={0}
              />
            </Flex>
          </Accordion.Content>
        </Accordion.Item>
      ) : (
        <Flex flexDirection="row" alignItems="center">
          <Box>
            <JaneGoldIcon size="lg" mr={16} />
          </Box>
          <Typography branded mr={getKioskSpacing(72, 24)}>
            You unlocked{' '}
            <Typography branded variant="body-semibold" as="span">
              {formatCurrency(brandDiscountsTotal)} cash back
            </Typography>
            !{' '}
            {!userSignedIn
              ? 'Sign in and complete your account to claim your reward.'
              : showJanePay
                ? 'Claim it by linking your bank to your Jane Pay account.'
                : null}
          </Typography>
          {(!userSignedIn || showJanePay) && (
            <Button
              branded
              label={!userSignedIn ? 'Sign in' : 'Link bank account'}
              variant="primary-inverse"
              onClick={!userSignedIn ? onLoginClick : onLinkBankClick}
              size={isIpad ? 'small' : 'default'}
              ml="auto"
            />
          )}
        </Flex>
      )}
    </Box>
  );
};
